<template>
  <div
    ref="columnsBar"
    :class="$.editing_view"
  >
    <columns-bar-header
      ref="header"
      :title="locale('columns_bar_editing_title')"
      :view-config="viewConfig"
      :back-button="!viewConfig.isBoard"
      :help-button="false"
      @onClose="$emit('onClose')"
      @backClick="backClick"
    />
    <div :class="$.body">
      <div
        v-if="warningMessage"
        ref="notification"
        :class="$.warning"
      >
        <inline-notification
          type="warning"
          :text="warningMessage"
          :font-size="12"
        />
      </div>
      <div
        v-if="viewConfig.currencyEditing"
        :class="$.currency_editing"
      >
        <div :class="$.switch">
          <div :class="$.label">
            {{ locale('budget_currency') }}
          </div>
          <toggle
            :is-toggle-on="enableCurrency"
            @onChange="currencySwitch"
          />
        </div>
        <div :class="$.slot">
          <vgp-label-slot :label="locale('budget_currency_sign')">
            <template #body>
              <dropdown-select
                :multiselect="false"
                :show-reset-btn="false"
                :disabled="!enableCurrency"
                :drop-list="currencies"
                :selected="[currentCurrency]"
                @selected="setSelected"
              />
            </template>
          </vgp-label-slot>
        </div>
      </div>
      <div
        v-else
        :class="$.custom_column_editing"
      >
        <div
          ref="input"
          :class="[$.slot, $.slot__name]"
        >
          <vgp-label-slot :label="locale('columns_bar_edit_name_input_label')">
            <template #body>
              <vgp-input
                v-model="name"
                :value="name"
                :small="true"
                :placeholder="placeholder"
                @input="inputName"
                @onChange="changeName"
              />
            </template>
          </vgp-label-slot>
        </div>

        <div
          ref="dropdown"
          :class="[$.slot, $.slot__custom_column]"
        >
          <vgp-label-slot :label="locale('columns_bar_new_column_types_select_label')">
            <template #body>
              <dropdown-select
                :drop-list="customColumnsTypes"
                :multiselect="false"
                :show-selected-first="false"
                :show-reset-btn="false"
                :highlight-active-state="false"
                item-template="withIcon"
                track-by-text="name"
                track-by-icon-name="icon"
                :disabled="true"
                :popup-max-height="305"
                :selected="getSelectedColumnType()"
                @selected="onSelectCustomColumnType"
              />
            </template>
          </vgp-label-slot>
        </div>

        <div
          v-if="columnType === 'select'"
          ref="options"
          :class="[$.slot, $.slot__options, $.slot__option_select]"
        >
          <vgp-label-slot :label="locale('columns_bar_select_options_label')">
            <template #body>
              <select-options
                :max-height="optionsListMaxHeight"
                :options="options"
                @onChange="changeOptions"
              />
            </template>
          </vgp-label-slot>
        </div>

        <div
          v-if="columnType === 'number'"
          :class="[$.slot, $.slot__option_number]"
        >
          <vgp-label-slot :label="locale('columns_bar_aggregations_options_label')">
            <template #body>
              <dropdown-select
                :drop-list="optionsAggregations"
                :multiselect="false"
                :show-selected-first="false"
                :show-reset-btn="false"
                :highlight-active-state="false"
                item-template="text"
                track-by-text="value"
                :popup-max-height="100"
                :selected="selectedOptions"
                @selected="changeOptions"
              />
            </template>
          </vgp-label-slot>
        </div>

        <div
          v-if="columnType === 'colors'"
          ref="options"
          :class="[$.slot, $.slot__options, $.slot__option_colors]"
        >
          <vgp-label-slot :label="locale('columns_bar_colors_options_label')">
            <template #body>
              <color-options
                :max-height="optionsListMaxHeight"
                :options="options"
                @onChange="changeOptions"
              />
            </template>
          </vgp-label-slot>
        </div>

        <div
          v-if="columnType === 'multiselect'"
          ref="options"
          :class="[$.slot, $.slot__options, $.slot__option_multiselect]"
        >
          <vgp-label-slot :label="locale('columns_bar_multiselect_options_label')">
            <template #body>
              <multiselect-options
                :max-height="optionsListMaxHeight"
                :options="options"
                @onChange="changeOptions"
              />
            </template>
          </vgp-label-slot>
        </div>

        <div
          v-if="columnType === 'resources'"
          :class="[$.slot, $.slot__option_resources]"
        >
          <vgp-label-slot :label="locale('columns_bar_resources_options_label')">
            <template #body>
              <dropdown-select
                :drop-list="preparedResources"
                :placeholder="locale('columns_bar_resources_select_placeholder')"
                :multiselect="true"
                :show-selected-first="false"
                :show-reset-btn="false"
                :highlight-active-state="false"
                :devider-color="'#999999'"
                :use-search="true"
                item-template="user"
                track-by-text="name"
                group-by="group"
                :use-select-all="true"
                :popup-max-height="200"
                :selected="selectedOptions"
                @change="changeOptions"
              />
            </template>
          </vgp-label-slot>
        </div>

        <div
          v-if="columnType === 'tags'"
          ref="options"
          :class="[$.slot, $.slot__options, $.slot__option_tags]"
        >
          <vgp-label-slot :label="locale('columns_bar_tags_options_label')">
            <template #body>
              <tag-options
                :max-height="optionsListMaxHeight"
                :options="options"
                @onChange="changeOptions"
              />
            </template>
          </vgp-label-slot>
        </div>
      </div>
    </div>
    <div
      v-if="!viewConfig.currencyEditing"
      ref="footer"
      :class="$.footer"
    >
      <div :class="[$.button, $.button__cancel]">
        <vgp-button
          id="gp_autotest_columns_bar_save_cancel_editing"
          :class="$.vgp"
          :label="locale('common_cancel')"
          type="secondary"
          @onClick="cancelChanges"
        />
      </div>
      <div :class="[$.button, $.button__save]">
        <vgp-button
          id="gp_autotest_columns_bar_save_editing"
          :class="$.vgp"
          :label="locale('common_save')"
          type="primary"
          @onClick="onClickSave"
        />
      </div>
    </div>
  </div>
</template>

<script>
import projectModel from '../../../../models/projects';
import columnsBarHeader from '../components/header/index.vue';

import DropdownSelect from '../../../common/VueComponents/dropdownSelect/dropdownSelect.vue';
import vgpLabelSlot from '../../../common/VueComponents/label/vgpLabelSlot.vue';
import vgpInput from '../../../common/VueComponents/vInput/vgpInput.vue';
import constants from '../constants';
import colorOptions from '../components/dropdownOptions/colors.vue';
import tagOptions from '../components/dropdownOptions/tags.vue';
import selectOptions from '../components/dropdownOptions/selectOptions.vue';
import multiselectOptions from '../components/dropdownOptions/multiselectOptions.vue';
import vgpButton from '../../../common/VueComponents/globalButton/vgpButton.vue';
import helpers from '../helpers';
import toggle from '../../../common/VueComponents/checkbox/squareToggle.vue';
import inlineNotification from '../../../common/VueComponents/nofitication/inline/InlineNotification.vue';

export default {
  name: 'ColumnsBarEditingView',
  components: {
    columnsBarHeader,
    DropdownSelect,
    vgpLabelSlot,
    vgpInput,
    vgpButton,
    colorOptions,
    tagOptions,
    selectOptions,
    multiselectOptions,
    toggle,
    inlineNotification,
  },
  props: {
    viewConfig: { type: Object, required: true, default: () => {} },
  },
  emits: {
    onClose: null,
    backClick: null,
    changeView: String,
    updateConfig: Object,
    showChangesConfirmation: Object,
    setUserChangesFlag: Boolean,
  },
  data() {
    return {
      locale: __,
      name: 'text',
      options: [],
      optionsListMaxHeight: 500,
      editedData: null,
      customColumnsTypes: constants.CUSTOM_COLUMNS,
      optionsAggregations: constants.OPTIONS_AGGREGATIONS_SELECT,
      isChangedOptions: false,
      enableCurrency: false,
      currentCurrency: null,
    };
  },
  computed: {
    withoutChanges() {
      if (this.viewConfig.currencyEditing) {
        const current = this.currencies.find(item => item.id === this.$store.getters['teamSetting/currency']);
        const enabledCurrency = this.$store.getters['teamSetting/enableCurrency'];

        if (!current || !this.currentCurrency) {
          return true;
        }

        if (current.id !== this.currentCurrency.id) {
          return false;
        }

        if (enabledCurrency !== this.enableCurrency) {
          return false;
        }

        return true;
      }

      if (this.name?.trim() === '') {
        return false;
      }

      if (!this.columnData) {
        return true;
      }

      return this.name?.trim() === this.columnData.name?.trim() && !this.isChangedOptions;
    },
    projectResources() {
      const projectId = projectModel.getActiveGanttId();

      return this.$store.getters['resourcesModel/getResourcesByProjectId'](projectId);
    },
    preparedResources() {
      const preparedResources = this.projectResources.map((item, index) => {
        const userPic = `${GT.cdn}/imgs/default/default-ava.svg`;
        const resource = {};

        resource.id = item.id;
        resource.img = item.photo || userPic;
        resource.name = item.name;
        resource.is_default = 0;
        resource.sort_order = index;

        if (item.userId) {
          resource.group = __('resources_layout_header_title_1');
        } else {
          resource.group = __('resources_layout_header_title_2');
        }

        return resource;
      });

      return preparedResources;
    },
    columnData() {
      if (this.viewConfig.columnId) {
        const stateColumn = this.$store.getters['columns/getCustomColumnById'](this.viewConfig.columnId);
        const defaultColumn = constants.CUSTOM_COLUMNS.find(col => col.id === stateColumn.type);

        stateColumn.defaultName = defaultColumn.name;

        return stateColumn;
      }

      return null;
    },
    placeholder() {
      if (this.columnData) {
        return this.columnData?.defaultName || '';
      }

      return '';
    },
    warningMessage() {
      const projectsWithColumns = this.$store.getters['columns/getProjectsNumberByColumnId'](this.viewConfig.columnId);

      let activeProjectsCountText = null;
      let archivedProjectsCountText = null;

      if (this.viewConfig.currencyEditing) {
        return __('warning_all_projects_setting');
      }

      if (projectsWithColumns.visible - 1 > 0) {
        activeProjectsCountText = __('columns_bar_warning_active_projects_count', { count: projectsWithColumns.visible - 1 });
      }

      if (projectsWithColumns.archive > 0) {
        archivedProjectsCountText = __('columns_bar_warning_archived_projects_count', { count: projectsWithColumns.archive });
      }

      if (activeProjectsCountText && archivedProjectsCountText) {
        return `${activeProjectsCountText} ${archivedProjectsCountText}`;
      }

      if (activeProjectsCountText && !archivedProjectsCountText) {
        return activeProjectsCountText;
      }

      if (archivedProjectsCountText && !activeProjectsCountText) {
        return archivedProjectsCountText;
      }

      return null;
    },
    columnType() {
      let type;

      if (!this.columnData?.type) {
        return null;
      }

      switch (this.columnData.type) {
      case 1:
        type = 'select';
        break;
      case 3:
        type = 'number';
        break;
      case 6:
        type = 'colors';
        break;
      case 7:
        type = 'multiselect';
        break;
      case 8:
        type = 'resources';
        break;
      case 9:
        type = 'tags';
        break;
      default:
        type = null;
      }

      return type;
    },
    selectedOptions() {
      let options = [];

      if (this.columnType === 'resources') {
        options = this.preparedResources.filter(res => this.options.find(opt => opt.resource_id === res.id));
      }

      if (this.columnType === 'number') {
        const numberSelectedOption = this.optionsAggregations.find(opt => opt.id === this.columnData.sub_type);

        options = [numberSelectedOption];
      }

      return options;
    },
    currencies() {
      return GT.currency.map(({ ID, VALUE }) => ({
        id: ID,
        name: `${VALUE} ${ID}`,
      }));
    },
  },
  watch: {
    columnData(data) {
      if (data) {
        // this.name = this.name.length ? this.name : data.name;
        this.name = data.name;

        const sorted = data.options.sort((a, b) => a.sort_order - b.sort_order);

        this.options = sorted;
      }
    },

    withoutChanges(flag) {
      if (!this.viewConfig.currencyEditing) {
        if (!flag) {
          this.$emit('setUserChangesFlag', true);
        } else {
          this.$emit('setUserChangesFlag', false);
        }
      }
    },

    'viewConfig.isArchivedProject': function (flag) {
      if (flag) {
        this.cancelChanges();
      }
    },
  },
  created() {
    if (this.columnData) {
      this.name = this.columnData.name;
      this.options = this.columnData.options;
    }
    this.currentCurrency = this.currencies.find(item => item.id === this.$store.getters['teamSetting/currency']) || this.currencies[0];
    this.enableCurrency = this.$store.getters['teamSetting/enableCurrency'];
  },
  mounted() {
    this.calcOptionsListMaxHeight();

    window.addEventListener('resize', this.calcOptionsListMaxHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcOptionsListMaxHeight);
    if (this.viewConfig.currencyEditing && !this.withoutChanges) {
      this.updateCurrency();
      this.$emit('updateConfig', { currencyEditing: false });

      return;
    }
    this.resetState();
  },
  methods: {
    backClick() {
      this.$emit('backClick');

      if (this.viewConfig.currencyEditing && !this.withoutChanges) {
        this.updateCurrency();
        this.$emit('updateConfig', { currencyEditing: false });
      }
    },
    updateCurrency() {
      this.$store.dispatch('teamSetting/setCurrency', {
        currency: this.currentCurrency.id,
        isActiveCurrency: this.enableCurrency,
      });
    },
    getSelectedColumnType() {
      if (this.columnData) {
        return this.customColumnsTypes.find(i => i.id === this.columnData.type);
      }

      return [];
    },
    inputName(value) {
      this.name = value;
    },
    changeName(e) {
      const value = e.target.value.trim();

      this.name = value;
    },
    changeOptions(options) {
      this.isChangedOptions = false;
      this.editedData = null;

      let newOptions = options;

      if (['select', 'multiselect', 'tags'].includes(this.columnType)) {
        newOptions = newOptions.filter(opt => opt.value !== '');
      }

      if (['resources'].includes(this.columnType) && !options.length) {
        const resourcesUpdatedData = helpers.prepareAllResourcesBeforeDelete(this.columnData);

        if (resourcesUpdatedData) {
          this.isChangedOptions = true;
          this.editedData = resourcesUpdatedData;
        }

        return;
      }

      if (!newOptions.length) {
        return;
      }

      const updatedData = helpers.prepareOptionsBeforeUpdate(this.columnType, this.columnData, newOptions);

      if (updatedData) {
        this.isChangedOptions = true;
        this.editedData = updatedData;
      }
    },
    onClickSave() {
      if (this.withoutChanges) {
        if (this.viewConfig.isBoard) {
          this.$emit('onClose');

          return;
        }

        this.$emit('backClick');
      } else {
        userExtAnalytics.log('columns_sidebar_save_changes_click');
        this.$emit('showChangesConfirmation', {
          ...this.editedData,
          name: this.name.length ? this.name : this.columnData.defaultName,
        });
      }
    },
    cancelChanges() {
      if (this.viewConfig.isBoard) {
        this.$emit('onClose');

        return;
      }

      this.$emit('updateConfig', { currencyEditing: false });
      this.$emit('changeView', 'columns', 'customColumns');
      this.resetState();
      this.$emit('setUserChangesFlag', false);
    },
    setSelected(selected) {
      this.currentCurrency = selected[0];
    },
    currencySwitch(e) {
      this.enableCurrency = !this.enableCurrency;
    },
    resetState() {
      // this.name = '';
      this.editedData = null;
      this.isChangedOptions = false;
      this.options = [];
      this.enableCurrency = false;
      this.currentCurrency = null;
    },
    calcOptionsListMaxHeight() {
      const options = this.$refs.options;

      if (options) {
        const columnsBar = this.$refs.columnsBar;
        const barFooter = this.$refs.footer;

        const optionsCoords = options.getBoundingClientRect();
        const columnsBarCoords = columnsBar.getBoundingClientRect();
        const topDiff = optionsCoords.top - columnsBarCoords.top;

        const optionsListMaxHeight = columnsBar.clientHeight - topDiff - barFooter.clientHeight - 50;

        this.optionsListMaxHeight = optionsListMaxHeight;
      }
    },
  },
};
</script>

<style module='$' lang='less'>
@import '../../../../less/_mixins.less';
@import '../../../../less/_fonts.less';

.editing_view {
  position: relative;
  background-color: #ffffff;
  box-shadow: -20px 0px 22px -22px rgba(0, 0, 0, 0.2);

  .body {
    padding-top: 0;
    padding-bottom: 12px;
    height: calc(100vh - 282px);

    .warning {
      padding: 0 12px;
      margin-bottom: 12px;
    }

    .slot {
      margin-bottom: 12px;

      &:not(.slot__options) {
        padding: 0 12px;
      }

      &__options {
        & > div > div:first-child {
          padding: 0 12px;
        }
      }
    }

    .currency_editing {
      padding: 0 12px;

      .switch {
        display: flex;
        justify-content: space-between;
        margin-bottom: 12px;

        .label {
          flex: 1 0 0;
          .FontLatoBold();
          .fs(14px, 24px);
          color: #999;
        }
      }

      .slot {
        padding: 0;
      }
    }
  }

  .footer {
    display: flex;
    align-items: center;
    padding: 12px;

    .button {
      .vgp > div {
        margin: 0 8px !important;
      }
    }

    .button__cancel {
      width: 100%;
      padding-right: 10px;
    }

    .button__save {
      width: 100%;
    }
  }
}
</style>
